import { createSlice } from "@reduxjs/toolkit";
import { ResourceDiary } from "../../models/ResourceDiary.model";
import {
  getResourceDiary,
  createOrUpdateResourceDiary,
  deleteResourceDiary,
} from "../thunks/resourceDiaryThunks";
import { getLeadData } from "../thunks/leadThunks";
import { projectSwitched } from "../actions/projectLifecycleActions";

interface ResourceDiaryState {
  // נתוני יומן משאבים
  resourceDiaries: ResourceDiary[];
  loading: boolean;
  error: string | null;

  // פעולות CRUD
  operationLoading: boolean;
  operationError: string | null;
  operationSuccess: boolean;
  operationMessage: string;

  // פרויקט נוכחי
  currentProjectId: number | null;

  // כללי
  initialized: boolean;
}

const initialState: ResourceDiaryState = {
  resourceDiaries: [],
  loading: false,
  error: null,

  operationLoading: false,
  operationError: null,
  operationSuccess: false,
  operationMessage: "",

  currentProjectId: null,

  initialized: false,
};

const resourceDiarySlice = createSlice({
  name: "resourceDiary",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.operationError = null;
      state.operationMessage = "";
      state.operationSuccess = false;
    },

    resetResourceDiaryState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(projectSwitched, (state, action) => {
        state.currentProjectId = action.payload;
        state.resourceDiaries = [];
        state.error = null;
        state.initialized = false;
        state.loading = false;
      })
      // Get Resource Diary
      .addCase(getResourceDiary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getResourceDiary.fulfilled, (state, action) => {
        state.loading = false;
        state.resourceDiaries = action.payload.resourceDiaries || [];
        state.initialized = true;
      })
      .addCase(getResourceDiary.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch resource diary";
      })
      .addCase(getLeadData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLeadData.fulfilled, (state, action) => {
        state.loading = false;
        state.resourceDiaries =
          action.payload.lead.diaryResource.map((diary) => ({
            resourceDiaryID: diary.id,
            resourceDiaryStartTime: diary.startTime,
            resourceDiaryEndTime: diary.endTime,
            resourceDiaryRemarks: diary.remark,
            resourceDiaryStatus: 0,
            resourceID: diary.resourceID,
            resourceName: diary.resourceName,
            resourceMinutesOffset: 0,
            resourceAllowOverflow: true,
            resourceColor: diary.color,
          })) || [];
        state.initialized = true;
      })
      .addCase(getLeadData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch resource diary";
      })
      // Create/Update Resource Diary
      .addCase(createOrUpdateResourceDiary.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(createOrUpdateResourceDiary.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const isUpdate = requestData.resourceDiaryID > 0;

        if (isUpdate) {
          // עדכון רשומה קיימת
          const index = state.resourceDiaries.findIndex(
            (rd) => rd.resourceDiaryID === requestData.resourceDiaryID,
          );
          if (index !== -1) {
            state.resourceDiaries[index] = {
              ...state.resourceDiaries[index],
              resourceDiaryStartTime: requestData.startTime,
              resourceDiaryEndTime: requestData.endTime,
              resourceDiaryRemarks: requestData.remarks,
              resourceID: requestData.resourceID,
              resourceName:
                requestData.resourceName ||
                state.resourceDiaries[index].resourceName,
            };
          }
        } else {
          // הוספת רשומה חדשה
          const newDiary: ResourceDiary = {
            resourceDiaryID: action.payload.resourceDiaryID,
            resourceDiaryStartTime: requestData.startTime,
            resourceDiaryEndTime: requestData.endTime,
            resourceDiaryRemarks: requestData.remarks,
            resourceDiaryStatus: 0,
            resourceID: requestData.resourceID,
            resourceName: requestData.resourceName || "משאב לא ידוע",
            resourceMinutesOffset: 0,
            resourceAllowOverflow: true,
            resourceColor: "blue",
          };
          state.resourceDiaries.push(newDiary);
        }
      })
      .addCase(createOrUpdateResourceDiary.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) ||
          "Failed to create/update resource diary";
      })
      .addCase(deleteResourceDiary.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(deleteResourceDiary.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        // הסר את הרשומה מהרשימה
        const resourceDiaryID = action.meta.arg.resourceDiaryID;
        state.resourceDiaries = state.resourceDiaries.filter(
          (rd) => rd.resourceDiaryID !== resourceDiaryID,
        );
      })
      .addCase(deleteResourceDiary.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to delete resource diary";
      });
  },
});

export const { clearMessages, resetResourceDiaryState } =
  resourceDiarySlice.actions;

export default resourceDiarySlice.reducer;

// Selectors
export const selectResourceDiaries = (state: {
  resourceDiary: ResourceDiaryState;
}) => state.resourceDiary.resourceDiaries || [];
export const selectResourceDiaryLoading = (state: {
  resourceDiary: ResourceDiaryState;
}) => state.resourceDiary.loading;
export const selectResourceDiaryError = (state: {
  resourceDiary: ResourceDiaryState;
}) => state.resourceDiary.error;
export const selectResourceDiaryInitialized = (state: {
  resourceDiary: ResourceDiaryState;
}) => state.resourceDiary.initialized;

export const selectOperationLoading = (state: {
  resourceDiary: ResourceDiaryState;
}) => state.resourceDiary.operationLoading;
export const selectOperationError = (state: {
  resourceDiary: ResourceDiaryState;
}) => state.resourceDiary.operationError;
export const selectOperationSuccess = (state: {
  resourceDiary: ResourceDiaryState;
}) => state.resourceDiary.operationSuccess;
export const selectOperationMessage = (state: {
  resourceDiary: ResourceDiaryState;
}) => state.resourceDiary.operationMessage;

export const selectCurrentProjectId = (state: {
  resourceDiary: ResourceDiaryState;
}) => state.resourceDiary.currentProjectId;

// Computed selectors
export const selectResourceDiaryByResourceId =
  (resourceId: number) => (state: { resourceDiary: ResourceDiaryState }) =>
    state.resourceDiary.resourceDiaries.filter(
      (rd) => rd.resourceID === resourceId,
    );

export const selectResourceDiaryById =
  (diaryId: number) => (state: { resourceDiary: ResourceDiaryState }) =>
    state.resourceDiary.resourceDiaries.find(
      (rd) => rd.resourceDiaryID === diaryId,
    );

export const selectResourceDiariesByDateRange =
  (startDate: Date, endDate: Date) =>
  (state: { resourceDiary: ResourceDiaryState }) =>
    state.resourceDiary.resourceDiaries.filter(
      (rd) =>
        new Date(rd.resourceDiaryStartTime) >= startDate &&
        new Date(rd.resourceDiaryEndTime) <= endDate,
    );
